<template>
    <div class="tile-content">
        <table class="table" v-if="hasItems">
            <colgroup>
                <col style="width:200px;">
                <col style="width:100px;">
                <col>
                <col style="width: 150px;">
            </colgroup>
            <thead>
            <tr>
                <th>
                    <div class="grid-fitted">
                        <div class="grid-item">
                            Name
                        </div>
                    </div>
                </th>
                <th>
                    <div class="grid-fitted">
                        <div class="grid-item">
                            Status
                        </div>
                    </div>
                </th>
                <th>
                    <div class="grid-fitted">
                        <div class="grid-item">
                            Filters
                        </div>
                    </div>
                </th>
                <th>
                </th>
            </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(rule, ruleIndex) in items"
                    :key="ruleIndex">
                    <td>
                        {{rule.name}}
                    </td>
                    <td>
                        <v-toggle
                            :value="true"
                            @input="toggleStatus"
                            v-model="rule.status"/>
                    </td>
                    <td>
                        <condition-summary
                            class="grid-fitted"
                            :criteria="rule.criteria"
                            :field-provider="fieldProvider">
                            <span slot="no-content" class="text-muted">This notification will always be sent.</span>
                        </condition-summary>
                    </td>
                    <td class="text-right"
                        v-if="$auth.emailNotifications.edit.isAllowed">
                        <v-icon-button
                            type="basic"
                            tooltip="Clone"
                            @click="cloneRule(rule)"
                            class="inline-s">
                            <v-icon icon="clone"/>
                        </v-icon-button>
                        <v-icon-button
                            type="basic"
                            tooltip="Edit"
                            @click="editRule(rule)"
                            class="inline-s">
                            <v-icon icon="edit"/>
                        </v-icon-button>
                        <v-icon-button
                            type="basic"
                            tooltip="Delete"
                            @click="deleteRule(rule)">
                            <v-icon icon="trash"/>
                        </v-icon-button>
                    </td>
                </tr>
            </tbody>
        </table>
        <v-no-content v-else>
            <span slot="title">It seems that you have no notifications.</span>
            <p slot="description">
                Create a new notification to get started
            </p>
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages[addRoute.name].isAllowed"
                    :to="addRoute"
                    class="btn btn-link">Add Notification<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-no-content>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import RuleFactory from '@/common/models/rules/rule-factory';
    import {
        vIcon, vIconButton, vToggle,
    } from '@/components/form';
    import vNoContent from '@/components/v-no-content';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import Base58 from '@/common/helpers/base58';
    import ConditionSummary from '@/modules/rules-engine/components/condition-summary/condition-summary';
    import FieldProvider from '@/modules/rules-engine/components/condition-builder/field-provider';
    import getFieldsFromPathways from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';
    import resolveFilterType from '@/components/query/helpers/resolve-filter-type';

    export default {
        name: 'notification-list',
        components: {
            vIcon,
            vIconButton,
            vToggle,
            vNoContent,
            ConditionSummary,
        },
        props: {
            value: {
                required: true,
                type: Array,
            },
            event: {
                type: EventModel,
            },
        },
        computed: {
            addRoute() {
                return {name: `add-notification`};
            },
            hasItems() {
                return this.items.length > 0;
            },
            fieldProvider() {
                return new FieldProvider({
                    pathwayResolver: getFieldsFromPathways,
                    typeResolver: resolveFilterType,
                    pathways: this.event.pathways,
                    customFieldsStore: this.$store.getters['rulesEngine/customFields'],
                });
            },
        },
        data() {
            return {
                items: [...this.value],
            };
        },
        methods: {
            toggleStatus() {
                this.triggerUpdate(`Notification status has been updated successfully.`);
            },
            triggerUpdate(message = null) {
                this.$emit('input', {
                    notifications: this.items.map(item => item.getPayload()),
                    message,
                });
            },
            async cloneRule(rule) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Clone notification "${rule.name}"`,
                    content: `Clone "${rule.name}" into a new notification? It will be turned off by default.`,
                    confirmLabel: 'Clone',
                };
                if (await confirmDialog(dialogParams)) {
                    const clonedRule = RuleFactory.create('binds', rule.getPayload());
                    clonedRule.name = `Copy of ${rule.name}`;
                    clonedRule.status = false;
                    this.items.splice(rule.order + 1, 0, clonedRule);
                    this.triggerUpdate('Notification has been cloned successfully.');
                }
            },
            editRule(rule) {
                const itemIndex = Base58.encode(rule.order);
                const name = 'edit-notification';
                this.$router.push({
                    name,
                    params: {
                        itemIndex,
                        eventType: this.$route.params.eventType,
                    },
                });
            },
            async deleteRule(rule) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Delete notification "${rule.name}"`,
                    content: 'Are you sure you want to delete this notification?',
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    this.items.splice(rule.order, 1);
                    this.triggerUpdate('Notification has been deleted successfully.');
                }
            },
        },
    };
</script>
